import { ButtonType } from 'inquiry/components/ButtonBase';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { FileType } from 'lib/file';
import { State, Style, StyleKey, StyleKeyToValueType } from 'lib/inquiryStyles';
import { ApiModel } from 'lib/interfaces/api';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import { DocumentConfigDocumentsUpload } from 'lib/interfaces/documentConfig';
import type {
  ButtonComponentStyles,
  ImageLocalComponentStyles,
  ImageRemoteComponent,
  TextComponentStyles,
  TitleComponentStyles,
  UiComponent,
} from 'lib/interfaces/ui-components';
import {
  GovernmentIdEnabledIdClassByCountryEntry,
  GovernmentIdSide,
  GovIdCapturePage,
  SelfieMatch,
} from 'lib/interfaces/verificationConfig';
import {
  DocumentCaptureOptionDesktop,
  DocumentCaptureOptionMobile,
  GovernmentIdCaptureFileType,
  GovernmentIdCaptureOptionDesktop,
  GovernmentIdCaptureOptionMobile,
  GovernmentIdFileType,
  GovernmentIdVideoCaptureMethod,
  SelfieCaptureFileType,
  SelfieCaptureOptionDesktop,
  SelfieCaptureOptionMobile,
  SelfieVideoCaptureMethod,
} from 'verify/lib/interfaces/CaptureOption';
import { SelfieStep as SelfiePose, SelfieThreePointPoseOrder } from 'verify/lib/interfaces/selfie';

export type VerificationStepType =
  | ApiModel.VerificationDatabase
  | ApiModel.VerificationDatabaseEcbsv
  | ApiModel.VerificationEmailAddress
  | ApiModel.VerificationGovernmentId
  | ApiModel.VerificationPhoneNumber
  | ApiModel.VerificationSelfie;

export enum UiStepType {
  // Dynamic Flow steps. Should not really be needed in the future if all steps are just 'ui'.
  Ui = 'ui',
  Document = 'document',
  GovernmentId = 'government_id',
  Selfie = 'selfie',
  SelfieReflection = 'selfie_reflection',
  Complete = 'complete',
  Integration = 'integration',
  VerifyWithPersona = 'verify_with_persona',
  Loop = 'loop',
  // Inquiry 2.0 steps
  Start = 'start',
  Completed = 'completed',
  Failed = 'failed',
  CountrySelect = 'country_select',
  DocumentsUpload = 'documents_upload',
}

// default names for stubbing combined steps on the client
export enum ClientStepKeys {
  Document = `verification_document`,
  GovernmentId = `verification_government_id`,
  Selfie = `verification_selfie`,
  SelfieReflection = `verification_selfie_reflection`,
  VerifyWithPersona = `verify_with_persona`,
}

export type StepName = string;
export type StepType = VerificationStepType | UiStepType;

export interface StepsConfig {
  'initial-step-name': StepName;
  steps: Step[];
}

export type StepConfig =
  | StepCompleteConfig
  | StepUiConfig
  | StepGovernmentIdConfig
  | StepSelfieConfig
  | StepSelfieReflectionConfig
  | StepDocumentConfig
  | StepIntegrationConfig
  | StepVerifyWithPersonaConfig
  | StepLoopConfig
  | DocumentConfigDocumentsUpload
  | StepConfigCompleted
  | StepConfigFailed;

export type CombinedStepClientData =
  | StepGovernmentId
  | StepSelfie
  | StepSelfieReflection
  | StepDocument;
export type CombinedStepClientConfig =
  | StepGovernmentIdConfig
  | StepSelfieConfig
  | StepSelfieReflectionConfig
  | StepDocumentConfig;
export type StepPreview =
  | StepUi
  | StepComplete
  | CombinedStepClientData
  | StepIntegration
  | StepVerifyWithPersona
  | StepLoop;

export function isCombinedStep(step: StepPreview | undefined): step is CombinedStepClientData {
  switch (step?.type) {
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
      return true;
    default:
      return false;
  }
}

export function isIntegrationStep(step: StepPreview | undefined): step is StepIntegration {
  switch (step?.type) {
    case UiStepType.Integration:
      return true;
    default:
      return false;
  }
}

export function isVerifyWithPersonaStep(
  step: StepPreview | undefined,
): step is StepVerifyWithPersona {
  switch (step?.type) {
    case UiStepType.VerifyWithPersona:
      return true;
    default:
      return false;
  }
}

export function isUiStep(step: StepPreview | undefined): step is StepUi {
  switch (step?.type) {
    case UiStepType.Ui:
      return true;
    default:
      return false;
  }
}

type StepStyles = StepUiStyles;

export interface StepOnStart {
  passed: StepName;
}

export interface StepOnCountrySelect {
  basic: StepName;
  'non-basic': StepName;
}

export interface StepOnDocumentsUpload {
  success: StepName;
}

export interface StepOnVerification {
  passed: StepName;
  failed: StepName;
  // requires-retry is optional to handle historical templates not having this step.
  'requires-retry'?: StepName;
}

export type StepOn = StepOnStart | StepOnCountrySelect | StepOnDocumentsUpload | StepOnVerification;

export interface StepBase {
  name: StepName;
  'name-display'?: string;
  type: StepType;
  on?: StepOn | never;
  config?: StepConfig | never;
  styles?: StepStyles | never;
}

export interface CombinedStepBase extends Omit<StepBase, 'styles'> {
  styles?: StepGovernmentIdStyles | StepSelfieStyles | BaseStepStyles | never;
}

export interface StepUi extends StepBase {
  name: StepName;
  type: UiStepType.Ui;
  config: StepUiConfig;
  styles?: StepUiStyles;
}

export interface StepDocument extends StepBase {
  name: StepName;
  type: UiStepType.Document;
  config: StepDocumentConfig;
  styles?: StepDocumentStyles;
}

export interface StepGovernmentId extends CombinedStepBase {
  name: StepName;
  type: UiStepType.GovernmentId;
  config: StepGovernmentIdConfig;
  styles?: StepGovernmentIdStyles;
}

export interface StepSelfie extends CombinedStepBase {
  name: StepName;
  type: UiStepType.Selfie;
  config: StepSelfieConfig;
  styles?: StepSelfieStyles;
}

export interface StepSelfieReflection extends CombinedStepBase {
  name: StepName;
  type: UiStepType.SelfieReflection;
  config: StepSelfieReflectionConfig;
  styles?: BaseStepStyles;
}

export interface StepComplete extends StepBase {
  name: StepName;
  type: UiStepType.Complete;
  config: StepCompleteConfig;
}

export interface StepIntegration extends StepBase {
  name: StepName;
  type: UiStepType.Integration;
  config: StepIntegrationConfig;
}

export interface StepVerifyWithPersona extends StepBase {
  name: StepName;
  type: UiStepType.VerifyWithPersona;
  config: StepVerifyWithPersonaConfig;
}

export interface StepLoop extends StepBase {
  name: StepName;
  type: UiStepType.Loop;
  config: StepLoopConfig;
}

/**
 * Cloned from front-end/lib/interfaces/verificationConfig.ts
 * Collection config should live in step
 */
export enum Disclaimer {
  Default = 'default',
  None = 'none',
}

export type PlatformLocalizations = {
  'cancel-dialog': {
    'btn-resume': string;
    'btn-submit': string;
    prompt: string;
    title: string;
  };
  'new-device-fraud-warning-dialog': {
    'btn-continue': string;
    'btn-cancel': string;
    prompt: string;
    title: string;
  };
  'unsupported-browser-warning-dialog': {
    'btn-continue': string;
    prompt: string;
    title: string;
  };
  'phishing-signal-dialog': {
    'btn-continue': string;
    'btn-learn-more': string;
    prompt: string;
    title: string;
  };
};

export type DeviceHandoffPromptPageLocalizations = {
  title: string;
  description: string;
  'qr-code': string;
  'via-email': string;
  'via-sms': string;
};

export type DeviceHandoffStartedPageLocalizations = {
  title: string;
  prompt: string;
  'btn-resume': string;
};

export type DeviceHandoffEmailPromptPageLocalizations = {
  title: string;
  prompt: string;
  'btn-submit': string;
  disclaimer?: string;
};

export type DeviceHandoffSmsPromptPageLocalizations = {
  title: string;
  prompt: string;
  'btn-submit': string;
  disclaimer?: string;
};

export type DeviceHandoffSmsSentPageLocalizations = {
  title: string;
  prompt: string;
  'btn-resend': string;
};

export type DeviceHandoffEmailSentPageLocalizations = {
  title: string;
  prompt: string;
  'btn-resend': string;
};

export type DeviceHandoffLocalizations = {
  'device-handoff-prompt-page': DeviceHandoffPromptPageLocalizations;
  'device-handoff-started-page': DeviceHandoffStartedPageLocalizations;
  'device-handoff-email-prompt-page': DeviceHandoffEmailPromptPageLocalizations;
  'device-handoff-sms-prompt-page': DeviceHandoffSmsPromptPageLocalizations;
  'device-handoff-sms-sent-page': DeviceHandoffSmsSentPageLocalizations;
  'device-handoff-email-sent-page': DeviceHandoffEmailSentPageLocalizations;
};

export type DeviceHandoffAssets = {
  'device-handoff-prompt-page': {
    'header-pictograph': ImageRemoteComponent | null;
    'icon-email': ImageRemoteComponent | null;
    'icon-sms': ImageRemoteComponent | null;
  };
  'device-handoff-started-page': {
    'header-pictograph': ImageRemoteComponent | null;
    'device-pictograph': ImageRemoteComponent | null;
  };
  'device-handoff-email-prompt-page': {
    'header-pictograph': ImageRemoteComponent | null;
  };
  'device-handoff-email-sent-page': {
    'header-pictograph': ImageRemoteComponent | null;
    'email-pictograph': ImageRemoteComponent | null;
  };
  'device-handoff-sms-prompt-page': {
    'header-pictograph': ImageRemoteComponent | null;
  };
  'device-handoff-sms-sent-page': {
    'header-pictograph': ImageRemoteComponent | null;
    'sms-pictograph': ImageRemoteComponent | null;
  };
};

export interface StepCombinedBaseConfig {
  'back-step-enabled': boolean;
  'cancel-button-enabled': boolean;
  'device-handoff-options': DeviceHandoffOption[];
  'device-handoff-list-style'?: 'outline' | 'outline_light' | 'button';
  'device-handoff-email-prefill': string;
  'device-handoff-email-disabled': boolean;
  'device-handoff-phone-number-prefill': string;
  'device-handoff-phone-number-disabled': boolean;
  localizations: PlatformLocalizations & Record<string, Record<string, string>>;
  'web-camera-manual-capture-delay-ms': number | null;
  'pending-page-text-vertical-position': 'start' | 'end' | null;
}

export type DocumentAllowedFileType = Exclude<
  FileType,
  FileType.ImageSvgXml | FileType.TextPlain | FileType.ImageGif
>;

export interface StepDocumentConfig extends StepCombinedBaseConfig {
  'enabled-capture-options-desktop': DocumentCaptureOptionDesktop[];
  'enabled-capture-options-mobile': DocumentCaptureOptionMobile[];
  'field-key-document': string;
  /**
   * Relies on the client to create a document. Will be null if no document has
   * been created yet.
   */
  'document-id': string | null;
  /**
   * Indicates which combined step page the client should start on.
   */
  'start-page': 'prompt' | 'review';
  kind: string;
  'document-file-limit': number;
  'accepted-file-types': DocumentAllowedFileType[];
  localizations: PlatformLocalizations &
    DeviceHandoffLocalizations & {
      'capture-page': {
        'btn-take-photo': string;
        'btn-additional-photo': string;
        'btn-finish': string;
        prompt: string;
      };
      'check-page': {
        'btn-retake': string;
        'btn-submit': string;
        'confirm-upload-btn-retake': string;
        'confirm-upload-btn-submit': string;
        'confirm-upload-prompt': string;
        'confirm-upload-title': string;
        'confirm-upload-uploading': string;
        'large-file-upload': string;
        'large-image-upload': string;
        prompt: string;
        title: string;
        uploading: string;
      };
      'pending-page': {
        title: string;
        description: string;
      };
      'prompt-page': {
        'btn-cancel': string;
        'btn-capture': string;
        'btn-capture-mobile-camera': string;
        'btn-delete': string;
        'btn-device-handoff': string;
        'btn-submit': string;
        'btn-upload': string;
        'camera-permissions-btn-continue-mobile': string;
        'camera-permissions-btn-continue-web': string;
        'camera-permissions-btn-cancel': string;
        'camera-permissions-prompt': string;
        'camera-permissions-title': string;
        'capture-options-dialog-title': string;
        'photos-permissions-body': string;
        'photos-permissions-dismiss-button-text': string;
        'photos-permissions-enable-button-text': string;
        'photos-permissions-settings-button-text': string;
        'photos-permissions-settings-text': string;
        'photos-permissions-title': string;
        'delete-dialog-title': string;
        'no-capture-options-prompt': string;
        prompt: string;
        title: string;
      };
    };
  assets: DeviceHandoffAssets & {
    'prompt-page': {
      'header-pictograph': ImageRemoteComponent | null;
      'document-pictograph': ImageRemoteComponent | null;
    };
    'pending-page': {
      'header-pictograph': ImageRemoteComponent | null;
      'loading-pictograph': ImageRemoteComponent | null;
    };
  };
  pages?: {
    document: {
      prompt: {
        'component-name-mapping': {
          'button-camera': string | null;
          'button-device-handoff': string | null;
          'button-file-picker': string | null;
          'button-photo-library': string | null;
          'button-upload-options': string | null;
        };
        'ui-step': StepUi;
      };
      'upload-options-dialog': {
        'component-name-mapping': {
          'button-camera': string | null;
          'button-cancel': string | null;
          'button-device-handoff': string | null;
          'button-file-picker': string | null;
          'button-photo-library': string | null;
        };
        'ui-step': StepUi;
      };
    };
  };
}

export type GovernmentIdClassLocalizations = {
  cct: string;
  cid: string;
  dl: string;
  foid: string;
  hic: string;
  id: string;
  ipp: string;
  keyp: string;
  ltpass: string;
  mid: string;
  munid: string;
  myn: string;
  nbi: string;
  nric: string;
  ofw: string;
  pan: string;
  pid: string;
  pp: string;
  ppc: string;
  pr: string;
  rp: string;
  sss: string;
  td: string;
  tribalid: string;
  umid: string;
  unknown: string;
  'us-id': string;
  vid: string;
  visa: string;
  wp: string;
};

export type GovernmentIdLocalizations = DeviceHandoffLocalizations & {
  'capture-page': {
    'auto-capture-off': string;
    'auto-capture-on': string;
    'btn-help': string;
    'btn-take-photo': string;
    capturing: string;
    'confirm-capture': string;
    'scan-back': string;
    'scan-front-or-back': string;
    'scan-front': string;
    'scan-mrz': string;
    'scan-pdf417': string;
    'scan-signature': string;
    'id-front-capture-frame-label': string;
    'id-back-capture-frame-label': string;
    'barcode-capture-frame-label': string;
    'passport-portrait-capture-frame-label': string;
    'passport-signature-capture-frame-label': string;
    'barcode-help-modal-title': string;
    'barcode-help-modal-prompt': string;
    'barcode-help-modal-warning': string;
    'barcode-help-modal-hints': string;
    'barcode-help-modal-continue-btn': string;
    'id-front-help-modal-title': string;
    'id-front-help-modal-prompt': string;
    'id-front-help-modal-hints-desktop': string;
    'id-front-help-modal-hints-mobile': string;
    'id-front-help-modal-continue-btn': string;
    'id-back-help-modal-title': string;
    'id-back-help-modal-prompt': string;
    'id-back-help-modal-hints-desktop': string;
    'id-back-help-modal-hints-mobile': string;
    'id-back-help-modal-continue-btn': string;
    'prompt-override': string;
    title: string;
  };
  'check-page': {
    title: string;
    'title-confirm-capture': string;
    prompt: string;
    uploading: string;
    'large-upload': string;
    'btn-submit': string;
    'btn-retake': string;
    'confirm-title': string;
    'confirm-prompt': string;
    'confirm-uploading': string;
    'confirm-btn-submit': string;
    'confirm-btn-retake': string;
    'id-front-capture-frame-label': string;
    'id-back-capture-frame-label': string;
    'barcode-capture-frame-label': string;
    'passport-portrait-capture-frame-label': string;
    'passport-signature-capture-frame-label': string;
  };
  'pending-page': {
    title: string;
    description: string;
  };
  'prompt-page': {
    'title-override': string;
    'prompt-override': string;
    'btn-capture': string;
    'btn-capture-mobile-camera': string;
    'btn-device-handoff': string;
    'btn-upload': string;
    'camera-permissions-btn-continue-mobile': string;
    'camera-permissions-btn-continue-web': string;
    'camera-permissions-prompt': string;
    'camera-permissions-title': string;
    'camera-and-mic-permissions-btn-continue-web': string;
    'camera-and-mic-permissions-prompt': string;
    'camera-and-mic-permissions-title': string;
    'photos-permissions-body': string;
    'photos-permissions-dismiss-button-text': string;
    'photos-permissions-enable-button-text': string;
    'photos-permissions-settings-button-text': string;
    'photos-permissions-settings-text': string;
    'photos-permissions-title': string;
    'no-capture-options-prompt': string;
  };
  'select-page': {
    'option-notice': string;
    prompt: string;
    title: string;
    'unsupported-title': string;
    'unsupported-prompt': string;
  } & GovernmentIdClassLocalizations;
  'auto-classification-page': {
    'unable-to-classify-document-title': string;
    'unable-to-classify-document-continue-button-text': string;
    'id-class-rejected-title': string;
    'id-class-rejected-continue-button-text': string;
    'country-input-title': string;
    'id-class-input-title': string;
    'manual-classification-title': string;
    'manual-classification-continue-button-text': string;
    'auto-classification-capture-tip-text': string;
  };
};

export interface GovernmentIdLocalizationOverride {
  page: keyof StepGovernmentIdConfig['localizations'];
  key: string;
  'country-code': CountryCode | null;
  'id-class': GovernmentIdClass | null;
  side: GovernmentIdSide | null;
  text: string;
}

export interface AutoClassificationConfig {
  'is-enabled': boolean;
  'extract-text-from-image': boolean;
  'capture-page-config': GovIdCapturePage;
  'skip-to-country-and-id-selection': boolean;
}

export interface StepGovernmentIdConfig extends StepCombinedBaseConfig {
  disclaimer: Disclaimer;
  idclasses: GovernmentIdEnabledIdClassByCountryEntry[];
  'barcode-camera-manual-capture-delay-ms': number | null;
  'camera-error-device-handoff-options': DeviceHandoffOption[];
  'enabled-skip-unknown': boolean;
  'field-key-country': string;
  'field-key-document': string;
  'field-key-idclass': string;
  'enabled-capture-options-desktop': GovernmentIdCaptureOptionDesktop[];
  'enabled-capture-options-mobile': GovernmentIdCaptureOptionMobile[];
  'always-show-mobile-camera-capture-option': boolean;
  'govid-design-version': number;
  'enabled-capture-file-types': GovernmentIdCaptureFileType[];
  'enabled-file-types': GovernmentIdFileType[];
  'audio-enabled': boolean;
  'video-capture-methods': GovernmentIdVideoCaptureMethod[];
  'idclass-list-style'?: 'outline' | 'outline_light' | 'button';
  'video-session-jwt': string | null;
  localizations: PlatformLocalizations & GovernmentIdLocalizations;
  'localization-overrides': GovernmentIdLocalizationOverride[];
  'auto-classification-config': AutoClassificationConfig | null;
  assets: DeviceHandoffAssets & {
    'select-page': {
      'header-pictograph': ImageRemoteComponent | null;
      'icon-drivers-license': ImageRemoteComponent | null;
      'icon-passport': ImageRemoteComponent | null;
      'icon-national-id': ImageRemoteComponent | null;
      'icon-permanent-residence-id': ImageRemoteComponent | null;
      'icon-government-id': ImageRemoteComponent | null;
    };
    'prompt-page': {
      'header-pictograph': ImageRemoteComponent | null;
      'id-front-pictograph': ImageRemoteComponent | null;
      'id-back-pictograph': ImageRemoteComponent | null;
      'passport-front-pictograph': ImageRemoteComponent | null;
      'passport-signature-pictograph': ImageRemoteComponent | null;
    };
    'capture-page': {
      'id-front-pictograph': ImageRemoteComponent | null;
      'id-back-pictograph': ImageRemoteComponent | null;
      'passport-front-pictograph': ImageRemoteComponent | null;
      'passport-signature-pictograph': ImageRemoteComponent | null;
      'barcode-pdf417-pictograph': ImageRemoteComponent | null;
      'id-front-help-modal-pictograph': ImageRemoteComponent | null;
      'id-back-help-modal-pictograph': ImageRemoteComponent | null;
      'barcode-help-modal-pictograph': ImageRemoteComponent | null;
    };
    'check-page': {
      'icon-file': ImageRemoteComponent | null;
    };
    'pending-page': {
      'header-pictograph': ImageRemoteComponent | null;
      'loading-pictograph': ImageRemoteComponent | null;
    };
  };
  'selected-country-code': CountryCode | null;
}

export interface PoseConfig {
  'manual-capture-enabled': boolean;
  'manual-capture-delay-ms': number;
  'auto-capture-enabled': boolean;
  'allow-review': boolean;
  pose: SelfiePose;
}

export interface StepSelfieConfig extends StepCombinedBaseConfig {
  'selfie-type': Exclude<SelfieMatch, SelfieMatch.Reflection>;
  'field-key-selfie': string;
  'web-camera-allow-stillfie-fallback': boolean;
  'enable-mobile-pending-experience-for-web': boolean;
  'enabled-capture-options-desktop': SelfieCaptureOptionDesktop[];
  'enabled-capture-options-mobile': SelfieCaptureOptionMobile[];
  'enabled-capture-file-types': SelfieCaptureFileType[];
  'audio-enabled': boolean;
  'video-capture-methods': SelfieVideoCaptureMethod[];
  'skip-prompt-page': boolean;
  'video-session-jwt': string | null;
  'ordered-poses': SelfieThreePointPoseOrder;
  'pose-configs'?: PoseConfig[];
  localizations: PlatformLocalizations &
    DeviceHandoffLocalizations & {
      'capture-page': {
        title: string;
        'selfie-hint-center-delayed': string;
        'selfie-hint-center-face': string;
        'selfie-hint-hold-still': string;
        'selfie-hint-look-left': string;
        'selfie-hint-look-right': string;
        'selfie-hint-face-too-close': string;
        'selfie-hint-face-too-far': string;
        'selfie-hint-multiple-faces': string;
        'selfie-hint-pose-not-center': string;
        'selfie-hint-take-photo': string;
        'selfie-hint-verifying': string;
      };
      'pending-page': {
        title: string;
        description: string;
      };
      'prompt-page': {
        'agree-to-policy': string;
        'btn-device-handoff': string;
        'btn-submit': string;
        'selfie-title': string;
        'selfie-prompt': string;
        'selfie-prompt-center': string;
        'selfie-prompt-left': string;
        'selfie-prompt-right': string;
        'stillfie-title': string;
        'stillfie-hint-left': string;
        'stillfie-hint-right': string;
        'stillfie-hint-wrong': string;
        'no-capture-options-prompt': string;
        'camera-permissions-btn-continue-mobile': string;
        'camera-permissions-btn-continue-web': string;
        'camera-permissions-btn-cancel': string;
        'camera-permissions-prompt': string;
        'camera-permissions-title': string;
        'microphone-permissions-btn-continue-mobile': string;
        'microphone-permissions-btn-continue-web': string;
        'microphone-permissions-btn-cancel': string;
        'microphone-permissions-prompt': string;
        'microphone-permissions-title': string;
      };
      'check-page': {
        title: string;
        description: string;
        'selfie-label-front': string;
        'selfie-label-left': string;
        'selfie-label-right': string;
        'btn-submit': string;
        'btn-retake': string;
      };
    };
  assets: DeviceHandoffAssets & {
    'prompt-page': {
      'header-pictograph': ImageRemoteComponent | null;
      'selfie-pictograph': ImageRemoteComponent | null;
      'selfie-center-pictograph': ImageRemoteComponent | null;
      'selfie-left-pictograph': ImageRemoteComponent | null;
      'selfie-right-pictograph': ImageRemoteComponent | null;
    };
    'record-page': {
      'loading-pictograph': ImageRemoteComponent | null;
      'selfie-left-pictograph': ImageRemoteComponent | null;
      'selfie-right-pictograph': ImageRemoteComponent | null;
    };
  };
}

export interface StepSelfieReflectionConfig extends StepCombinedBaseConfig {
  'selfie-type': SelfieMatch.Reflection;
  'field-key-selfie': string;
  'enabled-capture-options-desktop': SelfieCaptureOptionDesktop[];
  'enabled-capture-options-mobile': SelfieCaptureOptionMobile[];
  assets: DeviceHandoffAssets;
  localizations: PlatformLocalizations & DeviceHandoffLocalizations;
}

export interface StepCompleteConfig {
  localizations: PlatformLocalizations & {
    'child-flow-page': {
      title: string;
      prompt: string;
    };
  };
  terminal?: boolean;
}

export interface StepUiConfig {
  'back-step-enabled': boolean;
  'cancel-button-enabled': boolean;
  components: UiComponent[];
  initial?: boolean;
  localizations: PlatformLocalizations;
  'server-component-errors'?: { name: string; message: string }[];
  'server-step-messages'?: string[];
  terminal?: boolean;
}

export type IntegrationType = 'mx_bank' | 'kica' | 'aadhaar' | 'finnish_trust_network' | 'one_id';

interface StepIntegrationConfigBase {
  type: IntegrationType;
  'back-step-enabled': boolean;
  'cancel-button-enabled': boolean;
  localizations?: PlatformLocalizations;
}

export interface StepIntegrationConfigMxBank extends StepIntegrationConfigBase {
  type: 'mx_bank';
  'widget-url': string;
}

export interface StepIntegrationConfigKica extends StepIntegrationConfigBase {
  type: 'kica';
  'flow-url': string;
}

export interface StepIntegrationConfigAadhaar extends StepIntegrationConfigBase {
  type: 'aadhaar';
  'flow-url': string;
}

export interface StepIntegrationConfigFinnishTrustNetwork extends StepIntegrationConfigBase {
  type: 'finnish_trust_network';
  'flow-url': string;
}

export interface StepIntegrationConfigOneId extends StepIntegrationConfigBase {
  type: 'one_id';
  'flow-url': string;
}

export type StepIntegrationConfig =
  | StepIntegrationConfigMxBank
  | StepIntegrationConfigKica
  | StepIntegrationConfigAadhaar
  | StepIntegrationConfigFinnishTrustNetwork
  | StepIntegrationConfigOneId;

export interface StepVerifyWithPersonaConfig {
  'back-step-enabled': boolean;
  'cancel-button-enabled': boolean;
  pages: {
    'passkey-authentication': {
      'ui-step': StepUi;
      'component-name-mapping': {
        'confirm-button': string;
        'cancel-button': string;
      };
    };
    success: {
      'ui-step': StepUi;
      'component-name-mapping': {
        'continue-button': string;
      };
    };
  };
  localizations?: PlatformLocalizations;
}

export interface StepLoopConfig {
  'has-many-steps': boolean;
  steps: StepPreview[];
  localizations?: PlatformLocalizations; // Should be empty
}

/**
 * Styles found on all UI steps and combined steps.
 */
export interface BaseStepStyles {
  padding: Style<StyleKey.Padding, 'modal'>;
  'text-color': Style<StyleKey.TextColor, 'header-button'> &
    Style<StyleKey.TextColor, 'capture-overlay-header-button'>;
  'border-color': Style<StyleKey.BorderColor, 'header'>;
  'border-radius': Style<StyleKey.BorderRadius, 'modal'>;
  'border-width': Style<StyleKey.BorderWidth, 'header'>;
  'background-color': Style<StyleKey.BackgroundColor> &
    Style<StyleKey.BackgroundColor, 'header', State.Base> &
    Style<StyleKey.BackgroundColor, 'option', State.Base | State.Active | State.Focused>;
  'fill-color': Style<StyleKey.FillColor, 'base' | 'highlight' | 'background'>;
  'stroke-color': Style<StyleKey.StrokeColor>;
  'button-primary-style': Style<StyleKey.ButtonPrimaryStyle>;
  'button-secondary-style': Style<StyleKey.ButtonSecondaryStyle>;
  'title-style': Style<StyleKey.TitleStyle>;
  'text-style': Style<StyleKey.TextStyle>;
  'disclaimer-style': Style<StyleKey.DisclaimerStyle>;
  logo: Style<StyleKey.LogoStyle>;
}

export type StepUiStyles = BaseStepStyles;

/**
 * FIXME: This serialization shape does not allow for interaction style variants.
 * Will need to fix this shape to account for hover / focus etc., if needed.
 *
 * Get rid of these functions and just use the original object -- adding an
 * extra mapping layer on top doesn't provide value and makes it hard to keep
 * consistency.
 */
export type SerializedBaseStepStyles = {
  base?: {
    backgroundColor: BaseStepStyles['background-color']['base']['base'];
    headerButton?: {
      text: {
        color: BaseStepStyles['text-color']['header-button']['base'];
      };
    };
    header?: {
      border: {
        color: BaseStepStyles['border-color']['header']['base'];
        width: BaseStepStyles['border-width']['header']['base'];
      };
    };
    modal: {
      backgroundColor: BaseStepStyles['background-color']['base']['base'];
      padding: BaseStepStyles['padding']['modal']['base'];
      border: {
        radius: BaseStepStyles['border-radius']['modal']['base'];
      };
    };
    fill: {
      color: BaseStepStyles['fill-color']['base']['base'];
      highlightColor: BaseStepStyles['fill-color']['highlight']['base'];
      backgroundColor: BaseStepStyles['fill-color']['background']['base'];
    };
    stroke: {
      color: BaseStepStyles['stroke-color']['base']['base'];
    };
    titleStyle: TitleComponentStyles;
    textStyle: TextComponentStyles;
    disclaimerStyle: TextComponentStyles;
  };
  buttons?: SerializedButtonStyles;
};

export type SerializedButtonStyles = {
  [ButtonType.Primary]?: ButtonComponentStyles;
  [ButtonType.Secondary]?: ButtonComponentStyles;
};

export interface StepDocumentStyles extends BaseStepStyles {
  'border-color': Style<StyleKey.BorderColor, 'base' | 'header' | 'image-preview'>;
  'border-width': Style<StyleKey.BorderWidth, 'base' | 'header' | 'image-preview'>;
  'border-radius': Style<StyleKey.BorderRadius, 'modal' | 'image-preview'>;
  'fill-color': Style<
    StyleKey.FillColor,
    | 'base'
    | 'highlight'
    | 'background'
    | 'image-preview-plus-icon'
    | 'image-preview-x-icon'
    | 'image-preview-main-area'
    | 'image-preview-crop-area'
  >;
  'stroke-color': Style<
    StyleKey.StrokeColor,
    'base' | 'image-preview-plus-icon' | 'image-preview-x-icon'
  >;
  'title-style': Style<StyleKey.TitleStyle, 'base' | 'document-processing-title'>;
  'text-style': Style<StyleKey.TextStyle, 'base' | 'document-processing-text' | 'image-name'>;
}

/**
 * FIXME: This serialization shape does not allow for interaction style variants.
 * Will need to fix this shape to account for hover / focus etc., if needed.
 *
 * Get rid of these functions and just use the original object -- adding an
 * extra mapping layer on top doesn't provide value and makes it hard to keep
 * consistency.
 */
export type SerializedStepDocumentStyles = {
  base?: SerializedBaseStepStyles['base'] & {
    border: {
      color: StepDocumentStyles['border-color']['base'];
      width: StepDocumentStyles['border-width']['base'];
    };
  };
  buttons?: SerializedButtonStyles;
  imagePreview?: {
    border: {
      color: StepDocumentStyles['border-color']['image-preview']['base'];
      radius: StepDocumentStyles['border-radius']['image-preview']['base'];
      width: StepDocumentStyles['border-width']['image-preview']['base'];
    };
    plusIcon: {
      stroke: {
        color: StepDocumentStyles['stroke-color']['image-preview-plus-icon']['base'];
      };
      fill: {
        color: StepDocumentStyles['fill-color']['image-preview-plus-icon']['base'];
      };
    };
    xIcon: {
      stroke: {
        color: StepDocumentStyles['stroke-color']['image-preview-x-icon']['base'];
      };
      fill: {
        color: StepDocumentStyles['fill-color']['image-preview-x-icon']['base'];
      };
    };
    mainArea: {
      fill: {
        color: StepDocumentStyles['fill-color']['image-preview-main-area']['base'];
      };
    };
    cropArea: {
      fill: {
        color: StepDocumentStyles['fill-color']['image-preview-crop-area']['base'];
      };
    };
    imageName: {
      textStyle: TextComponentStyles;
    };
  };
  processingPage?: {
    textStyle: TextComponentStyles;
    titleStyle: TitleComponentStyles;
  };
};

export interface StepGovernmentIdStyles extends BaseStepStyles {
  'border-color': Style<
    StyleKey.BorderColor,
    | 'base'
    | 'header'
    | 'government-id-select-option'
    | 'government-id-capture-feed-box'
    | 'government-id-review-image-box'
  >;
  'border-width': Style<
    StyleKey.BorderWidth,
    | 'base'
    | 'header'
    | 'government-id-select-option'
    | 'government-id-capture-feed-box'
    | 'government-id-review-image-box'
  >;
  'border-radius': Style<
    StyleKey.BorderRadius,
    'modal' | 'government-id-capture-feed-box' | 'government-id-review-image-box'
  >;
  'fill-color': Style<
    StyleKey.FillColor,
    | 'base'
    | 'highlight'
    | 'background'
    | 'government-id-select-option-icon'
    | 'capture-page-hint-icon'
  >;
  'stroke-color': Style<
    StyleKey.StrokeColor,
    | 'base'
    | 'government-id-capture-feed-box'
    | 'government-id-select-option-icon'
    | 'government-id-select-option-chevron'
    | 'government-id-review-image-box'
    | 'capture-page-help-button-icon'
    | 'capture-page-hint-icon'
    | 'capture-overlay-header-button'
  >;
  height: Style<StyleKey.Height, 'government-id-select-option-min-row-height'>;
  'title-style': Style<StyleKey.TitleStyle, 'base' | 'government-id-processing-title'>;
  'text-style': Style<
    StyleKey.TextStyle,
    | 'base'
    | 'government-id-vertical-option'
    | 'government-id-capture-hint-text'
    | 'government-id-processing-text'
  >;
  'button-primary-style': Style<StyleKey.ButtonPrimaryStyle, 'base' | 'submit-photo-button'>;
  'button-secondary-style': Style<StyleKey.ButtonSecondaryStyle, 'base' | 'retake-photo-button'>;
  'input-select-style': Style<StyleKey.InputSelectStyle, 'base'>;
  'image-local-style': Style<StyleKey.ImageLocalStyle, 'illustration'>;
}

/**
 * FIXME: This serialization shape does not allow for interaction style variants.
 * Will need to fix this shape to account for hover / focus etc., if needed.
 *
 * Get rid of these functions and just use the original object -- adding an
 * extra mapping layer on top doesn't provide value and makes it hard to keep
 * consistency.
 */
export type SerializedStepGovernmentIdStyles = {
  base?: SerializedBaseStepStyles['base'] & {
    border: {
      color: StepGovernmentIdStyles['border-color']['base'];
      width: StepGovernmentIdStyles['border-width']['base'];
    };
  };
  buttons?: SerializedButtonStyles;
  captureButtons?: {
    submit?: ButtonComponentStyles;
    retake?: ButtonComponentStyles;
  };
  captureHelpButtonIcon?: {
    stroke: {
      color: StepGovernmentIdStyles['stroke-color']['capture-page-help-button-icon']['base'];
    };
  };
  captureOverlayHeaderButton?: {
    stroke: {
      color: StepGovernmentIdStyles['stroke-color']['capture-overlay-header-button']['base'];
    };
  };
  captureHint?: {
    textStyle: StepGovernmentIdStyles['text-style']['government-id-capture-hint-text']['base'];
  };
  captureHintIcon?: {
    stroke: {
      color: StepGovernmentIdStyles['stroke-color']['capture-page-hint-icon']['base'];
    };
    fill: {
      color: StepGovernmentIdStyles['fill-color']['capture-page-hint-icon']['base'];
    };
  };
  captureFeedBox?: {
    border: {
      color: StepGovernmentIdStyles['border-color']['government-id-capture-feed-box']['base'];
      radius: StepGovernmentIdStyles['border-radius']['government-id-capture-feed-box']['base'];
      width: StepGovernmentIdStyles['border-width']['government-id-capture-feed-box']['base'];
    };
    stroke: {
      color: StepGovernmentIdStyles['stroke-color']['government-id-capture-feed-box']['base'];
    };
  };
  processingPage?: {
    textStyle: TextComponentStyles;
    titleStyle: TitleComponentStyles;
  };
  reviewImageBox?: {
    border: {
      color: StepGovernmentIdStyles['border-color']['government-id-review-image-box']['base'];
      radius: StepGovernmentIdStyles['border-radius']['government-id-review-image-box']['base'];
      width: StepGovernmentIdStyles['border-width']['government-id-review-image-box']['base'];
    };
  };
  selectOption?: {
    backgroundColor: {
      base: StepGovernmentIdStyles['background-color']['base']['base'];
      active: StepGovernmentIdStyles['background-color']['option']['active'];
      focused: StepGovernmentIdStyles['background-color']['option']['focused'];
    };
    border: {
      color: StepGovernmentIdStyles['border-color']['government-id-select-option']['base'];
      width: StepGovernmentIdStyles['border-width']['government-id-select-option']['base'];
    };
    chevron: {
      stroke: {
        color: StepGovernmentIdStyles['stroke-color']['government-id-select-option-chevron']['base'];
      };
    };
    height: StepGovernmentIdStyles['height']['government-id-select-option-min-row-height']['base'];
    icon: {
      stroke: {
        color: StepGovernmentIdStyles['stroke-color']['government-id-select-option-icon']['base'];
      };
      fill: {
        color: StepGovernmentIdStyles['fill-color']['government-id-select-option-icon']['base'];
      };
    };
    textStyle: StepGovernmentIdStyles['text-style']['government-id-vertical-option']['base'];
  };
  inputSelect?: {
    base: StepGovernmentIdStyles['input-select-style']['base']['base'];
  };
  imageLocal?: {
    illustration: StepGovernmentIdStyles['image-local-style']['illustration']['base'];
  };
};

export interface StepSelfieStyles extends BaseStepStyles {
  'border-color': Style<StyleKey.BorderColor, 'header' | 'selfie-capture-feed-box'>;
  'border-width': Style<StyleKey.BorderWidth, 'header' | 'selfie-capture-feed-box'>;
  'fill-color': Style<
    StyleKey.FillColor,
    | 'base'
    | 'highlight'
    | 'background'
    | 'selfie-capture-icon-fill-color'
    | 'selfie-capture-icon-background-fill-color'
  >;
  'image-local-style': Style<StyleKey.ImageLocalStyle, 'selfie-start-icon'>;
  'stroke-color': Style<
    StyleKey.StrokeColor,
    'base' | 'selfie-capture-icon-stroke-color' | 'capture-complete-icon'
  >;
  'text-style': Style<
    StyleKey.TextStyle,
    'base' | 'selfie-capture-hint-text' | 'selfie-processing-text'
  >;
  'title-style': Style<StyleKey.TitleStyle, 'base' | 'selfie-processing-title'>;
}

/**
 * FIXME: This serialization shape does not allow for interaction style variants.
 * Will need to fix this shape to account for hover / focus etc., if needed.
 *
 * Get rid of these functions and just use the original object -- adding an
 * extra mapping layer on top doesn't provide value and makes it hard to keep
 * consistency.
 */
export type SerializedStepSelfieStyles = {
  base: SerializedBaseStepStyles['base'];
  buttons: SerializedButtonStyles;
  captureFeedbox: {
    border: {
      color?: StyleKeyToValueType[StyleKey.BorderColor];
      width?: StyleKeyToValueType[StyleKey.BorderWidth];
    };
  };
  captureHintText: TextComponentStyles;
  captureCompleteIcon: {
    stroke: {
      color?: StyleKeyToValueType[StyleKey.StrokeColor];
    };
  };
  captureIcon: {
    fill: {
      color?: StyleKeyToValueType[StyleKey.FillColor];
    };
    background: {
      color?: StyleKeyToValueType[StyleKey.FillColor];
    };
    stroke: {
      color?: StyleKeyToValueType[StyleKey.StrokeColor];
    };
  };
  processingPage?: {
    textStyle: TextComponentStyles;
    titleStyle: TitleComponentStyles;
  };
  startIcon: ImageLocalComponentStyles;
};

export type SerializedStepSelfieReflectionStyles = {
  base: SerializedBaseStepStyles['base'];
  buttons: SerializedButtonStyles;
};

export interface StepStart extends StepBase {
  name: StepName;
  type: UiStepType.Start;
  on: StepOnStart;
  config?: never;
}

export interface StepCompleted extends StepBase {
  name: StepName;
  type: UiStepType.Completed;
  on?: never;
  config?: StepConfigCompleted;
}

export type PictographOption = 'none' | 'default';

export interface StepConfigCompleted {
  pictograph?: PictographOption;
}

export interface StepFailed extends StepBase {
  name: StepName;
  type: UiStepType.Failed;
  on?: never;
  config?: StepConfigFailed;
}

export interface StepConfigFailed {
  'has-support': boolean;
  pictograph?: PictographOption;
}

export interface StepCountrySelect extends StepBase {
  name: StepName;
  type: UiStepType.CountrySelect;
  on: StepOnCountrySelect;
  config?: never;
}

export interface StepDocumentsUpload extends StepBase {
  name: StepName;
  type: UiStepType.DocumentsUpload;
  on: StepOnDocumentsUpload;
  config?: DocumentConfigDocumentsUpload;
}

interface StepVerificationCommon extends StepBase {
  name: StepName;
  type: VerificationStepType;
  on: StepOnVerification;
}

export interface StepVerificationEmailAddress extends StepVerificationCommon {
  type: ApiModel.VerificationEmailAddress;
  config?: never;
}

export interface StepVerificationGovernmentId extends StepVerificationCommon {
  type: ApiModel.VerificationGovernmentId;
  config?: any;
}

export interface StepVerificationPhoneNumber extends StepVerificationCommon {
  type: ApiModel.VerificationPhoneNumber;
  config?: any;
}

export interface StepVerificationSelfie extends StepVerificationCommon {
  type: ApiModel.VerificationSelfie;
  config?: any;
}

export interface StepVerificationDatabase extends StepVerificationCommon {
  type: ApiModel.VerificationDatabase;
  config?: any;
}

export interface StepVerificationDatabaseEcbsv extends StepVerificationCommon {
  type: ApiModel.VerificationDatabaseEcbsv;
  config?: any;
}

export type StepVerification =
  | StepVerificationDatabase
  | StepVerificationDatabaseEcbsv
  | StepVerificationEmailAddress
  | StepVerificationGovernmentId
  | StepVerificationSelfie;

export type CombinedStep = StepDocument | StepGovernmentId | StepSelfie | StepSelfieReflection;

export type DynamicFlowStep =
  | StepUi
  | CombinedStep
  | StepComplete
  | StepIntegration
  | StepVerifyWithPersona
  | StepLoop;

export type Step =
  // Dynamic Flow
  | DynamicFlowStep
  // Inquiry 2.0
  | StepStart
  | StepCompleted
  | StepFailed
  | StepCountrySelect
  | StepDocumentsUpload
  | StepVerificationCommon
  | StepVerification;

export type ConfigurableStep =
  | StepVerificationDatabase
  | StepVerificationDatabaseEcbsv
  | StepVerificationGovernmentId
  | StepVerificationSelfie;
